import React, { useState, useEffect } from 'react';
import { fmtDate, fmtDateTime } from '@shared/formatDate';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import { autoRefillAPI, paymentMethodsAPI } from '../../services/stripeAPI';

interface PaymentMethod {
  paymentMethodId: string;
  type: 'card' | 'bank_account';
  card?: {
    brand: string;
    last4: string;
  };
  bankAccount?: {
    bankName: string;
    last4: string;
  };
}

export default function AutoRefillSettings() {
  const [enabled, setEnabled] = useState(false);
  const [thresholdAmount, setThresholdAmount] = useState('50.00');
  const [refillAmount, setRefillAmount] = useState('200.00');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [settings, setSettings] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load both settings and payment methods
      const [settingsData, pmData] = await Promise.all([
        autoRefillAPI.getSettings(),
        paymentMethodsAPI.list()
      ]);

      if (settingsData.isEnabled) {
        setEnabled(true);
        setThresholdAmount(settingsData.thresholdAmount.toString());
        setRefillAmount(settingsData.refillAmount.toString());
        setSettings(settingsData);
      }

      setPaymentMethods(pmData.paymentMethods || []);
      
      if (pmData.paymentMethods && pmData.paymentMethods.length > 0) {
        setSelectedPaymentMethod(pmData.paymentMethods[0].paymentMethodId);
      }
    } catch (err: any) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const threshold = parseFloat(thresholdAmount);
    const refill = parseFloat(refillAmount);

    // Validation
    if (isNaN(threshold) || threshold < 10 || threshold > 1000) {
      setError('Threshold must be between $10 and $1,000');
      return;
    }

    if (isNaN(refill) || refill < 25 || refill > 5000) {
      setError('Refill amount must be between $25 and $5,000');
      return;
    }

    if (refill <= threshold) {
      setError('Refill amount must be greater than threshold');
      return;
    }

    if (!selectedPaymentMethod) {
      setError('Please select a payment method');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      if (enabled) {
        await autoRefillAPI.enable(threshold, refill, selectedPaymentMethod);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        await autoRefillAPI.disable();
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
      
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const calculateSavings = () => {
    const refill = parseFloat(refillAmount || '0');
    
    // Card fee: 2.9% + $0.30
    const cardFee = refill * 0.029 + 0.30;
    
    // ACH fee: 0.8% (capped at $5)
    const achFee = Math.min(refill * 0.008, 5);
    
    return {
      cardFee,
      achFee,
      savings: cardFee - achFee
    };
  };

  const renderPaymentMethodLabel = (pm: PaymentMethod) => {
    if (pm.type === 'card') {
      return `${pm.card!.brand} â€¢â€¢â€¢â€¢ ${pm.card!.last4}`;
    } else {
      return `${pm.bankAccount!.bankName} â€¢â€¢â€¢â€¢ ${pm.bankAccount!.last4} (ACH - Save money!)`;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const savings = calculateSavings();
  const hasACH = paymentMethods.some(pm => pm.type === 'bank_account');

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Auto-Refill</Typography>
          {enabled && settings && (
            <Chip label="ACTIVE" color="success" size="small" />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
          Automatically top off your account when balance falls below threshold
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Settings saved successfully!
          </Alert>
        )}

        {paymentMethods.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Please add a payment method first to enable auto-refill.
            </Typography>
          </Alert>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={paymentMethods.length === 0 || saving}
            />
          }
          label={
            <Typography variant="body1">
              Enable Auto-Refill
            </Typography>
          }
          sx={{ mb: 3 }}
        />

        {enabled && (
          <>
            <TextField
              fullWidth
              label="Trigger Threshold"
              type="number"
              value={thresholdAmount}
              onChange={(e) => setThresholdAmount(e.target.value)}
              helperText="Refill when balance falls below this amount ($10-$1,000)"
              InputProps={{
                startAdornment: '$',
              }}
              inputProps={{
                min: 10,
                max: 1000,
                step: 1,
              }}
              sx={{ mb: 2 }}
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Refill Amount"
              type="number"
              value={refillAmount}
              onChange={(e) => setRefillAmount(e.target.value)}
              helperText="Amount to add when refilling ($25-$5,000)"
              InputProps={{
                startAdornment: '$',
              }}
              inputProps={{
                min: 25,
                max: 5000,
                step: 1,
              }}
              sx={{ mb: 2 }}
              disabled={saving}
            />

            <TextField
              fullWidth
              select
              label="Payment Method"
              value={selectedPaymentMethod}
              onChange={(e) => setSelectedPaymentMethod(e.target.value)}
              helperText="Choose card or bank account (ACH is cheaper!)"
              sx={{ mb: 2 }}
              disabled={saving}
            >
              {paymentMethods.map((pm) => (
                <MenuItem key={pm.paymentMethodId} value={pm.paymentMethodId}>
                  {renderPaymentMethodLabel(pm)}
                </MenuItem>
              ))}
            </TextField>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>How it works:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                â€¢ When your balance drops below ${thresholdAmount}, we'll automatically add ${refillAmount}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                â€¢ Checked every hour
              </Typography>
              <Typography variant="body2" color="text.secondary">
                â€¢ You can disable anytime
              </Typography>
            </Box>

            {!hasACH && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  ðŸ’° Save up to 70% with ACH!
                </Typography>
                <Typography variant="body2">
                  Card fee: ${savings.cardFee.toFixed(2)} vs ACH fee: ${savings.achFee.toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  <strong>Save ${savings.savings.toFixed(2)} per refill</strong>
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => {/* Navigate to add bank account */}}
                >
                  Add Bank Account
                </Button>
              </Alert>
            )}

            {settings && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="body2" color="primary">
                  <strong>Statistics:</strong>
                </Typography>
                <Typography variant="body2">
                  â€¢ Last refill: {settings.lastRefillDate ? fmtDate(settings.lastRefillDate) : 'Never'}
                </Typography>
                <Typography variant="body2">
                  â€¢ Total refills: {settings.refillCount || 0}
                </Typography>
              </Box>
            )}
          </>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || paymentMethods.length === 0}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

